import {IMAGE_PIXEL_DENSITY_DPRS} from 'algolia_listing_pages/utils/algolia_constants';
import config from 'config';

// Constants
const CATALOG_PATH = '/mms/images/catalog/';
const CONFIG_FLAGS = new Set(['useLabImage', 'colorId', 'assetHash']);
const DEFAULT_HASH = 'default';
const HASH_REGEX = /\/catalog\/([a-z0-9]{32})\//;
const LAB_IMAGE_PATH = '/views/alt/front_medium_extended.png';
const SPECIAL_PARAMS = new Set([
  'imengrave',
  'imdeboss',
  'imemboss',
  'imengravesteel',
  'imengravewood',
  'imembroider',
  'debopacity',
  'noauto'
]);

// Converts a path or URL to a full MMS URL
function toMmsUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  let path = pathOrUrl;

  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      const url = new URL(pathOrUrl);
      path = url.pathname + (url.search || '');
    } catch {
      path = pathOrUrl;
    }
  }

  const base = config.images.mmsBaseUrl;
  const fullBasePath = `${base}${CATALOG_PATH}`;

  // If path already contains the full catalog path, just prepend the domain
  if (path.includes(CATALOG_PATH)) {
    return `${base}${path}`;
  }

  return `${fullBasePath}${path.startsWith('/') ? path.slice(1) : path}`;
}

// Adds or updates query parameters on a URL
function withParams(urlString, params = {}) {
  try {
    const url = new URL(urlString);
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') {
        url.searchParams.delete(k);
      } else {
        url.searchParams.set(k, String(v));
      }
    });
    return url.toString();
  } catch {
    // If malformed, return as-is
    return urlString;
  }
}

// Extracts hash from catalog image URL
function extractCatalogHash(imageUrl) {
  if (!imageUrl) return null;
  const match = imageUrl.match(HASH_REGEX);
  return match?.[1] ?? null;
}

function getEffectiveHash(colors, colorId) {
  if (colors && colorId) {
    const colorImage = colors.find(
      (color) => String(color.id) === String(colorId)
    );
    if (colorImage?.image_url) {
      const hash = extractCatalogHash(colorImage.image_url);
      if (hash) {
        return hash;
      }
    }
  }

  return DEFAULT_HASH;
}

// Helper to check if a parameter should be included
function shouldIncludeParam(key, value) {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  // Special handling for boolean-like rendering effect parameters - only include if value is 1
  if (SPECIAL_PARAMS.has(key)) {
    return value === 1;
  }

  return true;
}

// Helper to format parameter values
function formatParamValue(value) {
  if (typeof value === 'boolean') {
    return value ? 1 : undefined;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  return value === 1 ? 1 : undefined;
}

// Builds MMS parameters from image configuration
function buildImageParams(imageConfig) {
  if (!imageConfig || typeof imageConfig !== 'object') {
    return {};
  }

  return Object.entries(imageConfig)
    .filter(([key]) => !CONFIG_FLAGS.has(key))
    .reduce((params, [key, value]) => {
      if (shouldIncludeParam(key, value)) {
        const formattedValue = formatParamValue(value);
        if (formattedValue !== undefined) {
          params[key] = formattedValue;
        }
      }
      return params;
    }, {});
}

function generateSrcSet(imageUrl) {
  if (!imageUrl) {
    return '';
  }

  return Object.keys(IMAGE_PIXEL_DENSITY_DPRS)
    .map((key) => `${imageUrl} ${key}`)
    .join(', ');
}

/**
 * Resolves product image URLs from Algolia imageConfig objects
 * @param {Object} options
 * @param {Array} options.categoryImages - Category image objects
 * @param {string|number} [options.colorIdOverride = null] - Selected color ID (falls back to imageConfig.colorId if useLabImage is true)
 * @param {Array} options.colors - Color objects with id and image_url
 * @param {Object} options.imageConfig - Algolia config object
 * @param {string|number} options.primaryCategoryId - Primary category ID
 * @param {string} options.styleCardImageUrl - Style card image URL
 * @returns {Object} {src, srcset} URLs
 */
export default function algoliaImageResolver({
  colorIdOverride = null,
  colors,
  imageConfig,
  productId,
  styleCardImageUrl
}) {
  const useLabImage = imageConfig?.useLabImage || colorIdOverride !== null;
  const labImageColorId = imageConfig?.colorId || colors?.[0]?.id || productId;

  // Use fallback colorId from imageConfig if not provided but useLabImage is true
  const effectiveColorId =
    colorIdOverride ?? (useLabImage ? labImageColorId : null);

  if (useLabImage && imageConfig && effectiveColorId !== null) {
    const hash = getEffectiveHash(colors, effectiveColorId);
    const labImagePath = `/${hash}/colors/${effectiveColorId}${LAB_IMAGE_PATH}`;
    const params = buildImageParams(imageConfig);

    const labImageUrl = withParams(toMmsUrl(labImagePath), params);

    return {
      src: labImageUrl,
      srcset: generateSrcSet(labImageUrl)
    };
  }

  // Fallback to styleCardImageUrl (Laydown Image)
  if (styleCardImageUrl) {
    const fallbackUrl = toMmsUrl(styleCardImageUrl);
    return {
      src: fallbackUrl,
      srcset: generateSrcSet(fallbackUrl)
    };
  }

  return {
    src: '',
    srcset: ''
  };
}
